import { Box, Button, ButtonGroup, Card, Checkbox, FormControlLabel, IconButton, Modal, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '../../../app/AppStyles';
import { ConvertoToCSharpDateTime, CountWeekdaysInRange, DisplayName } from '../../../app/AppUtils';
import LocationEditCard from '../../management-activity/location/LocationEditCard';
import AutocompleteSelector from '../../subcomponents/AutocompleteSelector';
import ContentLoader from '../../subcomponents/ContentLoader';
import { fetchDayPlanning, fetchMonthPlanning, fetchPeriodPlanning, fetchPlanningActivities, fetchWeekPlanning, fetchYearPlanning } from '../planningSlice';
import { fetchDefaultActivity, fetchPerformance, postPerformance, putPerformance, resetPlanningPerformanceStatus, setFailedPerformance } from './planningPerformanceSlice';

import AddIcon from '@mui/icons-material/Add';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    width: { xs: '90%', md: '40%' },
    maxHeight: '90%',
    boxShadow: 24,
};

export default function PlanningPerformanceEditCard({ handleClose, addNew, addActivity, id, waitForInput, periodId }) {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { activeDepartment } = useSelector((state) => state.settings.theme);
    const { appSettings } = useSelector((state) => state.settings.app);
    const { allContacts } = useSelector((state) => state.contacts);
    const { newPlanningActivities, newPlanningActivitiesStatus, planningPeriods, accordionDisplay, accordionSelectedDate, accordionShowPast } = useSelector((state) => state.planning.planning);
    const { showFailed, performance, putPerformanceStatus, postPerformanceStatus, defaultActivity, defaultActivityStatus, planningLocations } = useSelector((state) => state.planning.performance);
    const { aspNetUser } = useSelector((state) => state.settings.app);

    // States
    const [changed, setChanged] = useState(false);
    const [openLocation, setOpenLocation] = useState(false);
    const [newActivity, setNewActivity] = useState(null);
    const [newPerformance, setNewPerformance] = useState({});
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [newPeriod, setNewPeriod] = useState(null);
    const [changedPeriod, setChangedPeriod] = useState(false);
    const [minMoment, setMinMoment] = useState(null);
    const [maxMoment, setMaxMoment] = useState(null);
    const [momentsAmount, setMomentsAmount] = useState();

    const [selectedMomentStart, setSelectedMomentStart] = useState(null);
    const [selectedMomentEnd, setSelectedMomentEnd] = useState(null);
    const [selectedSubscribableFrom, setSelectedSubscribableFrom] = useState(null);
    const [selectedSubscribableUntil, setSelectedSubscribableUntil] = useState(null);

    const [selectedUnsubscribableUntil, setSelectedUnsubscribableUntil] = useState(null);

    // Errors
    const [errorActivity, setErrorActivity] = useState(null);
    const [errorName, setErrorName] = useState(null);
    const [errorCapacity, setErrorCapacity] = useState(null);
    const [errorLocation, setErrorLocation] = useState(null);
    const [errorMomentStart, setErrorMomentStart] = useState(null);
    const [errorMomentEnd, setErrorMomentEnd] = useState(null);
    const [errorMomentRange, setErrorMomentRange] = useState(null);
    const [errorSubscribableUntil, setErrorSubscribableUntil] = useState(null);
    const [errorSubscribableFrom, setErrorSubscribableFrom] = useState(null);
    const [errorPeriod, setErrorPeriod] = useState(null);
    const [errorMoments, setErrorMoments] = useState(null);
    const [errorMomentPeriodRange, setErrorMomentPeriodRange] = useState(null);
    const [errorSubscribableRange, setErrorSubscribableRange] = useState(null);
    const [errorSubscribableMoment, setErrorSubscribableMoment] = useState(null);

    // Get default activity values
    useEffect(() => {
        if (addNew && performance?.activity?.id)
            dispatch(fetchDefaultActivity(performance?.activity?.id));
    }, [dispatch, addNew, performance]);

    // Set default moment amount
    useEffect(() => {
        if (addNew && selectedMomentStart && selectedPeriod) {
            let momentsAmount;

            if (siomSettings?.momentDefaultAmount && siomSettings?.momentDefaultAmount.toLowerCase() === "period" && selectedMomentStart && selectedPeriod) {
                momentsAmount = CountWeekdaysInRange(new Date(selectedMomentStart), new Date(selectedPeriod.endTime), selectedMomentStart.day());
            } else if (siomSettings?.momentDefaultAmount) {
                momentsAmount = siomSettings?.momentDefaultAmount;
            } else {
                momentsAmount = 1;
            }

            setNewPerformance({
                ...newPerformance,
                moments: momentsAmount
            });

            setMomentsAmount(momentsAmount)
        }
    }, [dispatch, addNew, selectedMomentStart, selectedPeriod]);

    // Get selected period values
    useEffect(() => {
        if (periodId) {
            let newPeriod = planningPeriods.find(x => x.periodId === periodId);

            setNewPeriod(newPeriod);
            setSelectedPeriod(newPeriod);
        }
    }, [periodId]);

    useEffect(() => {
        if (newPeriod) {
            setNewPerformance({
                ...newPerformance,
                period: newPeriod
            });

            setChangedPeriod(true);
        }
    }, [newPeriod]);

    // Get default partial payment amount
    useEffect(() => {
        if (addNew && siomSettings) {
            setNewPerformance({
                ...newPerformance,
                partialPaymentAmount: siomSettings?.defaultPartialPaymentAmount
            });
        }
    }, [siomSettings]);

    // Check min/max date for moment end
    useEffect(() => {
        if (selectedPeriod) {
            setMaxMoment(dayjs(selectedPeriod?.endTime));
            setMinMoment(dayjs(selectedPeriod?.startTime));
        };
    }, [selectedPeriod]);

    // Set 'subscribable from' if new performance
    useEffect(() => {
        if (addNew && selectedSubscribableFrom === null) {
            handleSubscribableFromChange();
        }
    }, []);

    // Set 'subscribable from' if period changed
    useEffect(() => {
        if (changedPeriod) {
            setChangedPeriod(false);
            handleSubscribableFromChange();
        }
    }, [minMoment]);


    // Set default activity values
    useEffect(() => {
        if (defaultActivityStatus === "success") {
            setNewPerformance({
                ...newPerformance,
                capacityLimit: defaultActivity?.capacity,
                points: siomSettings?.flagProgress ? 0 : defaultActivity?.points,
                cost: defaultActivity?.cost,
                contact: defaultActivity?.contact,
                location: defaultActivity?.location,
                studentRegistrationDisabled: defaultActivity?.nonSubscribable,
            });
        }
    }, [dispatch, addNew, performance, defaultActivityStatus, defaultActivity]);

    // Put status callback
    useEffect(() => {
        if (putPerformanceStatus === "success") {
            dispatch(setFailedPerformance(false));
            dispatch(resetPlanningPerformanceStatus());
            dispatch(fetchPerformance(id));
            handleClose();
        } else if (putPerformanceStatus === "failed") {
            dispatch(setFailedPerformance(true));
        };
    }, [dispatch, handleClose, putPerformanceStatus, id]);

    // Post status callback
    useEffect(() => {
        if (postPerformanceStatus === "success") {
            dispatch(setFailedPerformance(false));
            dispatch(resetPlanningPerformanceStatus());

            const departmentId = siomSettings.useMultipleDepartments && activeDepartment ? activeDepartment[0]?.id : null;

            if (accordionDisplay === "AccordionActivity")
                dispatch(fetchPlanningActivities({ showPast: accordionShowPast, departmentId: departmentId }));

            if (accordionDisplay === "AccordionDay")
                dispatch(fetchDayPlanning({ date: accordionSelectedDate, departmentId: departmentId }));

            if (accordionDisplay === "AccordionWeek")
                dispatch(fetchWeekPlanning({ date: accordionSelectedDate, departmentId: departmentId }));

            if (accordionDisplay === "AccordionMonth")
                dispatch(fetchMonthPlanning({ date: new Date(new Date(accordionSelectedDate).setMonth(new Date(accordionSelectedDate).getMonth() + 1)).toISOString(), departmentId: departmentId }));

            if (accordionDisplay === "AccordionYear")
                dispatch(fetchYearPlanning({ date: null, departmentId: departmentId }));

            if (accordionDisplay === "AccordionPeriod")
                dispatch(fetchPeriodPlanning({ periodId: selectedPeriod.periodId, departmentId: departmentId }));

            handleClose();
        } else if (postPerformanceStatus === "failed") {
            dispatch(setFailedPerformance(true));
        };
    }, [dispatch, handleClose, postPerformanceStatus]);

    // Character limits
    const nameLength = 255;

    // Set selected performance state 
    useEffect(() => {
        if (!addNew && performance) {
            setSelectedPeriod(performance.period);
            setNewPerformance(performance);

            setSelectedMomentStart(dayjs(performance?.momentStart));
            setSelectedMomentEnd(dayjs(performance?.momentEnd));
            setSelectedSubscribableFrom(dayjs(performance?.subscribableFrom));
            setSelectedSubscribableUntil(dayjs(performance?.subscribableUntil));
            setSelectedUnsubscribableUntil(dayjs(performance?.unsubscribableUntil));
        }
    }, [addNew, performance]);

    // Keep track of changed state 
    useEffect(() => {
        if ((!addNew && newPerformance !== performance) || (addNew && Object.keys(newPerformance) !== 0)) {
            setChanged(true);
        } else {
            setChanged(false);
        };
    }, [performance, newPerformance, addNew]);

    // Check moment range
    useEffect(() => {
        if (Date.parse(selectedMomentStart) >= Date.parse(selectedMomentEnd)) {
            setErrorMomentRange(true);
        } else {
            setErrorMomentRange(false);
        };
    }, [selectedMomentStart, selectedMomentEnd]);

    // Check subscribable date range
    useEffect(() => {
        if (Date.parse(selectedSubscribableFrom) >= Date.parse(selectedSubscribableUntil)) {
            setErrorSubscribableRange(true);
        } else {
            setErrorSubscribableRange(false);
        };
    }, [selectedSubscribableFrom, selectedSubscribableUntil]);

    // Check if subscribable before moment
    useEffect(() => {
        if (Date.parse(selectedSubscribableUntil) >= Date.parse(selectedMomentStart)) {
            setErrorSubscribableMoment(true);
        } else {
            setErrorSubscribableMoment(false);
        };
    }, [selectedSubscribableUntil, selectedMomentStart]);

    // Check if moments in period
    useEffect(() => {
        if (selectedMomentStart && selectedMomentEnd && newPerformance.moments && minMoment && maxMoment) {
            let isValid = true;

            for (let i = 0; i < newPerformance.moments; i++) {
                let amount = 7 * i;
                let startDateToCheck = new Date(selectedMomentStart);
                let endDateToCheck = new Date(selectedMomentEnd);

                startDateToCheck.setDate(startDateToCheck.getDate() + amount);
                endDateToCheck.setDate(endDateToCheck.getDate() + amount);

                if (!(
                    (Date.parse(startDateToCheck) >= Date.parse(minMoment) && Date.parse(startDateToCheck) <= Date.parse(maxMoment)) &&
                    (Date.parse(endDateToCheck) >= Date.parse(minMoment) && Date.parse(endDateToCheck) <= Date.parse(maxMoment))
                )) {
                    isValid = false;
                    break;
                }
            }

            setErrorMomentPeriodRange(!isValid);
        }
    }, [selectedMomentStart, selectedMomentEnd, minMoment, maxMoment, newPerformance.moments]);

    const callbackModalLocation = () => setOpenLocation(false);

    const handleSubscribableFromChange = () => {
        let defaultSubscriptionStart;
        const now = dayjs(new Date());

        if (minMoment) {
            if (siomSettings?.subscriptionDefaultStartTime && siomSettings?.subscriptionStartDaysForPeriod) {
                const [hours, minutes] = siomSettings?.subscriptionDefaultStartTime.split(":").map(Number);
                defaultSubscriptionStart = minMoment.subtract(siomSettings?.subscriptionStartDaysForPeriod, 'days');
                defaultSubscriptionStart = dayjs(defaultSubscriptionStart).hour(hours).minute(minutes).second(0);
            } else if (siomSettings?.subscriptionDefaultStartTime && !siomSettings?.subscriptionStartDaysForPeriod) {
                const [hours, minutes] = siomSettings?.subscriptionDefaultStartTime.split(":").map(Number);
                defaultSubscriptionStart = dayjs(now).hour(hours).minute(minutes).second(0);
            } else if (!siomSettings?.subscriptionDefaultStartTime && siomSettings?.subscriptionStartDaysForPeriod) {
                defaultSubscriptionStart = minMoment.subtract(siomSettings?.subscriptionStartDaysForPeriod, 'days');
                defaultSubscriptionStart = dayjs(defaultSubscriptionStart).hour(0).minute(0).second(0);
            } else {
                defaultSubscriptionStart = now;
            }

            setSelectedSubscribableFrom(defaultSubscriptionStart);
        }
    }

    // Determine subscribe untile datetime with database setting value
    const handleSubscribeUntil = (momentStart) => {
        const num = Number(siomSettings?.subscriptionDefaultEndTime);
        const isInt = Number.isInteger(num) && siomSettings?.subscriptionDefaultEndTime.trim() !== '';

        if (siomSettings?.subscriptionDefaultEndTime === 'activityStart') {
            return dayjs(momentStart).subtract(1, 'minute')
        } else if (isInt) {
            return dayjs(momentStart).subtract(siomSettings?.subscriptionDefaultEndTime, 'hours');
        } else {
            return dayjs(momentStart).subtract(24, 'hours');
        }
    }

    // Update click function (check property character limits etc.)
    const updateClick = async () => {
        let tempObject = { ...newPerformance }; // Needed to circumvent weird read only property error

        if (!newPerformance.period) return setErrorPeriod(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorPeriod(null);

        if (!newPerformance.name && appSettings?.appVersion === 2) return setErrorName(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorName(null);

        if (newPerformance.name && newPerformance.name?.length > nameLength && appSettings?.appVersion === 2) return setErrorName(`${resourceFinal[appLanguage].messages.ERROR_TOO_LONG} ${nameLength}`);
        setErrorName(null);

        if (!newPerformance.capacityLimit) return setErrorCapacity(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorCapacity(null);

        if (!newPerformance.location) return setErrorLocation(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorLocation(null);

        if (!selectedMomentStart) return setErrorMomentStart(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorMomentStart(null);

        if (!selectedMomentEnd) return setErrorMomentEnd(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorMomentEnd(null);

        if (!selectedSubscribableFrom) return setErrorSubscribableFrom(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorSubscribableFrom(null);

        if (!selectedSubscribableUntil) return setErrorSubscribableUntil(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorSubscribableUntil(null);

        if (errorMomentRange) return;
        if (errorSubscribableRange) return;
        if (errorSubscribableMoment) return;
        if (errorMomentPeriodRange) return;

        if (!tempObject?.contact?.employeeId) tempObject.contact = null;
        if (!tempObject?.primaryAttendant?.employeeId) tempObject.primaryAttendant = null;
        if (!tempObject?.secondaryAttendant?.employeeId) tempObject.secondaryAttendant = null;

        !newPerformance.hasOwnProperty('moments') ? tempObject.moments = 1 : tempObject.moments = newPerformance.moments;
        tempObject.momentStart = ConvertoToCSharpDateTime(selectedMomentStart);
        tempObject.momentEnd = ConvertoToCSharpDateTime(selectedMomentEnd);
        tempObject.subscribableFrom = ConvertoToCSharpDateTime(selectedSubscribableFrom);
        tempObject.subscribableUntil = ConvertoToCSharpDateTime(selectedSubscribableUntil);

        tempObject.unsubscribableUntil = siomSettings?.enableUnsubscribeDays ? ConvertoToCSharpDateTime(selectedUnsubscribableUntil) : null;

        tempObject.id = performance.id;
        tempObject.modifiedBy = `AP/${user.number}`;

        dispatch(putPerformance(tempObject));
    };

    // Add click function (check property character limits etc.)
    const addClick = async () => {
        let tempObject = { ...newPerformance }; // Needed to circumvent weird read only property error

        if (addActivity && !newActivity) return setErrorActivity(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorActivity(null);

        if (!newPerformance.period) return setErrorPeriod(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorPeriod(null);

        if (!newPerformance.moments) return setErrorMoments(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorMoments(null);

        if (!newPerformance.name && appSettings?.appVersion === 2) return setErrorName(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorName(null);

        if (newPerformance.name && newPerformance.name?.length > nameLength) return setErrorName(`${resourceFinal[appLanguage].messages.ERROR_TOO_LONG} ${nameLength}`);
        setErrorName(null);

        if (!newPerformance.capacityLimit) return setErrorCapacity(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorCapacity(null);

        if (!newPerformance.location) return setErrorLocation(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorLocation(null);

        if (!selectedMomentStart) return setErrorMomentStart(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorMomentStart(null);

        if (!selectedMomentEnd) return setErrorMomentEnd(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorMomentEnd(null);

        if (!selectedSubscribableFrom) return setErrorSubscribableFrom(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorSubscribableFrom(null);

        if (!selectedSubscribableUntil) return setErrorSubscribableUntil(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        setErrorSubscribableUntil(null);

        if (errorMomentRange) return;
        if (errorMomentPeriodRange) return;
        if (errorSubscribableRange) return;
        if (errorSubscribableMoment) return;

        !newPerformance.hasOwnProperty('moments') ? tempObject.moments = 1 : tempObject.moments = newPerformance.moments;
        tempObject.momentStart = ConvertoToCSharpDateTime(selectedMomentStart);
        tempObject.momentEnd = ConvertoToCSharpDateTime(selectedMomentEnd);
        tempObject.subscribableFrom = ConvertoToCSharpDateTime(selectedSubscribableFrom);
        tempObject.subscribableUntil = ConvertoToCSharpDateTime(selectedSubscribableUntil);

        tempObject.unsubscribableUntil = siomSettings?.enableUnsubscribeDays ? ConvertoToCSharpDateTime(selectedUnsubscribableUntil) : null;

        tempObject.activity = addActivity ? { id: newActivity } : performance.activity;
        tempObject.createdBy = `AP/${user.number}`;

        dispatch(postPerformance(tempObject));
    };

    return (
        <Box sx={{ ...modalStyle }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                <Scrollbars autoHeight autoHeightMin="100%" autoHeightMax="100%" autoHideTimeout={1000} autoHideDuration={200}>
                    <Grid container spacing={1}>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.PERFORMANCE} {addNew ? resourceFinal[appLanguage].words.ADD.toLowerCase() : resourceFinal[appLanguage].words.EDIT.toLowerCase()}</Typography>
                        </Grid>
                        {addActivity ? (
                            <ContentLoader
                                successCondition={newPlanningActivitiesStatus === 'success'}
                                errorCondition={newPlanningActivitiesStatus === 'failed'}
                                errorCompact
                                content={
                                    <Grid size={{ xs: 12 }}>
                                        <AutocompleteSelector
                                            label={resourceFinal[appLanguage].words.ACTIVITIES}
                                            options={newPlanningActivities ?? []}
                                            value={newActivity ? newPlanningActivities.find((x) => x.id === newActivity) : null}
                                            onChange={(val) => {
                                                setNewActivity(val?.id);
                                                dispatch(fetchDefaultActivity(val?.id));
                                            }}
                                            getOptionLabel={(option) => option.name}
                                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                            error={errorActivity}
                                            required
                                            disableClearable
                                        />
                                    </Grid>
                                }
                            />
                        ) : (
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth disabled required={appSettings?.appVersion === 2}
                                    value={performance?.activity?.name}
                                    label={resourceFinal[appLanguage].words.ACTIVITY}
                                    variant="outlined"
                                />
                            </Grid>
                        )}
                        <ContentLoader
                            waitForInput={waitForInput}
                            successCondition={addNew ? defaultActivityStatus === "success" : true}
                            errorCondition={addNew ? defaultActivityStatus === "failed" : false}
                            content={
                                <>
                                    <Grid size={{ xs: 12 }}>
                                        <AutocompleteSelector
                                            label={resourceFinal[appLanguage].words.PERIOD}
                                            options={planningPeriods ?? []}
                                            value={
                                                addNew ? planningPeriods.find((x) => x.periodId === periodId) :
                                                    newPerformance?.period ?? null
                                            }
                                            onChange={(val) => {
                                                setNewPerformance({
                                                    ...newPerformance,
                                                    period: val
                                                });
                                                setSelectedPeriod(val);
                                                setChangedPeriod(true);
                                            }}
                                            getOptionLabel={(option) => option.name}
                                            isOptionEqualToValue={(option, value) => option.periodId === value?.periodId}
                                            error={errorPeriod}
                                            required
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth required={appSettings?.appVersion === 2}
                                            defaultValue={performance?.name}
                                            label={resourceFinal[appLanguage].words.NAME}
                                            onChange={(event) => setNewPerformance({
                                                ...newPerformance,
                                                name: event.target.value
                                            })}
                                            error={errorName !== null}
                                            helperText={errorName}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                                            <DateTimePicker
                                                slotProps={{ textField: { fullWidth: true } }}
                                                disabled={!newPerformance.period}
                                                label={`${resourceFinal[appLanguage].words.MOMENT} ${resourceFinal[appLanguage].words.START.toLowerCase()} *`}
                                                value={selectedMomentStart ?? null}
                                                onChange={(value) => {
                                                    setChanged(true);
                                                    setSelectedMomentStart(dayjs(value));
                                                    if (addNew || Date.parse(value) > Date.parse(newPerformance.momentEnd)) setSelectedMomentEnd(dayjs(value).add(siomSettings?.momentDefaultDuration, 'minutes'));
                                                    if (addNew || Date.parse(value) < Date.parse(newPerformance.subscribableUntil)) setSelectedSubscribableUntil(handleSubscribeUntil(value));
                                                    if (addNew) setSelectedUnsubscribableUntil(dayjs(value).add(-siomSettings?.defaultUnsubscribeDays, 'days'));
                                                }}
                                                minDate={minMoment}
                                                maxDate={maxMoment}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                                            <DateTimePicker
                                                slotProps={{ textField: { fullWidth: true } }}
                                                disabled={!newPerformance.period}
                                                value={selectedMomentEnd ?? null}
                                                label={`${resourceFinal[appLanguage].words.MOMENT} ${resourceFinal[appLanguage].words.END.toLowerCase()} *`}
                                                onChange={(value) => {
                                                    setChanged(true);
                                                    setSelectedMomentEnd(dayjs(value));
                                                    if ((addNew && selectedMomentStart === null) || Date.parse(value) < Date.parse(newPerformance.momentStart)) setSelectedMomentStart(dayjs(value).subtract(siomSettings?.momentDefaultDuration, 'minutes'));
                                                }}
                                                minDate={minMoment}
                                                maxDate={maxMoment}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                                            <DateTimePicker
                                                slotProps={{ textField: { fullWidth: true } }}
                                                disabled={!selectedMomentStart}
                                                label={`${resourceFinal[appLanguage].words.SUBSCRIBABLE_FROM} *`}
                                                value={selectedSubscribableFrom ?? null}
                                                onChange={(value) => {
                                                    setChanged(true);
                                                    setSelectedSubscribableFrom(dayjs(value));
                                                }}
                                                maxDate={selectedMomentStart}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                                            <DateTimePicker
                                                slotProps={{ textField: { fullWidth: true } }}
                                                disabled={!selectedMomentStart}
                                                label={`${resourceFinal[appLanguage].words.SUBSCRIBABLE_UNTIL} *`}
                                                value={selectedSubscribableUntil ?? null}
                                                onChange={(value) => {
                                                    setChanged(true);
                                                    setSelectedSubscribableUntil(dayjs(value));
                                                }}
                                                maxDate={selectedMomentStart}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    {siomSettings?.enableUnsubscribeDays &&
                                        <Grid size={{ xs: 12, md: 12 }}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                                                <DateTimePicker
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                    disabled={!selectedMomentStart}
                                                    label={`${resourceFinal[appLanguage].words.UNSUBSCRIBED_DATE_MAX} *`}
                                                    value={selectedUnsubscribableUntil ?? null}
                                                    onChange={(value) => {
                                                        setChanged(true);
                                                        setSelectedUnsubscribableUntil(dayjs(value));
                                                    }}
                                                    maxDate={selectedMomentStart}
                                                />
                                            </LocalizationProvider>
                                        </Grid>}
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth required type="number"
                                            value={momentsAmount ?? ""}
                                            label={resourceFinal[appLanguage].words.MOMENTS}
                                            onChange={(event) => {
                                                let amount = Math.round(event.target.value);

                                                setMomentsAmount(amount);
                                                setNewPerformance({
                                                    ...newPerformance,
                                                    moments: amount
                                                });
                                            }}
                                            InputProps={{
                                                inputProps: { min: 1 }
                                            }}
                                            variant="outlined"
                                            disabled={!addNew || !selectedMomentStart}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth required type="number"
                                            defaultValue={addNew ? defaultActivity?.capacity : performance?.capacityLimit}
                                            label={resourceFinal[appLanguage].words.CAPACITY}
                                            onChange={(event) => setNewPerformance({
                                                ...newPerformance,
                                                capacityLimit: Math.round(event.target.value)
                                            })}
                                            error={errorCapacity !== null}
                                            helperText={errorCapacity}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    {!siomSettings?.flagProgress &&
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                fullWidth type="number"
                                                defaultValue={addNew ? defaultActivity?.points : performance?.points}
                                                label={appSettings?.appVersion === 1 ? resourceFinal[appLanguage].words.POINTS : resourceFinal[appLanguage].words.HOURS}
                                                onChange={(event) => setNewPerformance({
                                                    ...newPerformance,
                                                    points: Math.round(event.target.value)
                                                })}
                                                variant="outlined"
                                            />
                                        </Grid>}
                                    {appSettings?.appVersion === 1 &&
                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                fullWidth type="number"
                                                inputProps={{ step: 0.5 }}
                                                defaultValue={addNew ? defaultActivity?.cost : performance?.cost}
                                                label={resourceFinal[appLanguage].words.COST}
                                                onChange={(event) => setNewPerformance({
                                                    ...newPerformance,
                                                    cost: event.target.value
                                                })}
                                                variant="outlined"
                                            />
                                        </Grid>}
                                    {appSettings?.appVersion === 1 &&
                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                fullWidth type="number"
                                                inputProps={{ step: 0.5 }}
                                                defaultValue={addNew ? "" : performance?.extraCost}
                                                label={resourceFinal[appLanguage].words.COST_EXTRA}
                                                onChange={(event) => setNewPerformance({
                                                    ...newPerformance,
                                                    extraCost: event.target.value
                                                })}
                                                variant="outlined"
                                            />
                                        </Grid>}
                                    {(appSettings?.appVersion === 1 && siomSettings?.enablePartialPayments) &&
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControlLabel control={<Checkbox defaultChecked={performance?.partialPaymentPosible} onChange={(event) => {
                                                setNewPerformance({
                                                    ...newPerformance,
                                                    partialPaymentPosible: event.target.checked
                                                })
                                            }} />} label={`${resourceFinal[appLanguage].words.PARTIAL_PAYMENT} ${resourceFinal[appLanguage].words.POSSIBLE.toLowerCase()}?`} />
                                        </Grid>}
                                    {(appSettings?.appVersion === 1 && siomSettings?.enablePartialPayments) &&
                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                fullWidth type="number"
                                                inputProps={{ step: 1, min: 2 }}
                                                defaultValue={addNew ? siomSettings?.defaultPartialPaymentAmount : performance?.partialPaymentAmount}
                                                label={`${resourceFinal[appLanguage].words.PARTIAL_PAYMENT} ${resourceFinal[appLanguage].words.COUNT.toLowerCase()}`}
                                                onChange={(event) => setNewPerformance({
                                                    ...newPerformance,
                                                    partialPaymentAmount: event.target.value
                                                })}
                                                variant="outlined"
                                            />
                                        </Grid>}
                                    <Grid size={{ xs: 12 }}>
                                        <AutocompleteSelector
                                            label={resourceFinal[appLanguage].words.CONTACT}
                                            options={allContacts?.data ?? []}
                                            value={newPerformance.contact ?? null}
                                            onChange={(val) => {
                                                setNewPerformance({
                                                    ...newPerformance,
                                                    contact: val
                                                });
                                            }}
                                            getOptionLabel={(option) => DisplayName(option.firstname, option.insertion, option.lastname)}
                                            isOptionEqualToValue={(option, value) => option.employeeId === value?.employeeId}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <AutocompleteSelector
                                            label={`${resourceFinal[appLanguage].words.PRIMARY} ${resourceFinal[appLanguage].words.ATTENDANT.toLowerCase()}`}
                                            options={allContacts?.data ?? []}
                                            value={newPerformance.primaryAttendant ?? null}
                                            onChange={(val) => {
                                                setNewPerformance({
                                                    ...newPerformance,
                                                    primaryAttendant: val
                                                });
                                            }}
                                            getOptionLabel={(option) => DisplayName(option.firstname, option.insertion, option.lastname)}
                                            isOptionEqualToValue={(option, value) => option.employeeId === value?.employeeId}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <AutocompleteSelector
                                            label={`${resourceFinal[appLanguage].words.SECONDARY} ${resourceFinal[appLanguage].words.ATTENDANT.toLowerCase()}`}
                                            options={allContacts?.data ?? []}
                                            value={newPerformance.secondaryAttendant ?? null}
                                            onChange={(val) => {
                                                setNewPerformance({
                                                    ...newPerformance,
                                                    secondaryAttendant: val
                                                });
                                            }}
                                            getOptionLabel={(option) => DisplayName(option.firstname, option.insertion, option.lastname)}
                                            isOptionEqualToValue={(option, value) => option.employeeId === value?.employeeId}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Stack direction="row" spacing={0.5} sx={{ width: 1 }}>
                                            <AutocompleteSelector
                                                label={resourceFinal[appLanguage].words.LOCATION}
                                                options={planningLocations ?? []}
                                                value={newPerformance.location ?? null}
                                                onChange={(val) => {
                                                    setNewPerformance({
                                                        ...newPerformance,
                                                        location: val
                                                    });
                                                }}
                                                getOptionLabel={(option) => option.name}
                                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                                error={errorLocation}
                                                required
                                            />
                                            {aspNetUser?.role?.includes("Activities") &&
                                                <IconButton sx={{ height: 56, border: 1, borderRadius: 1, borderColor: 'white.dark' }} onClick={() => setOpenLocation(true)}><AddIcon color="primary" /></IconButton>
                                            }
                                        </Stack>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth multiline
                                            defaultValue={performance?.remark}
                                            label={resourceFinal[appLanguage].words.PERFORMANCE_REMARK}
                                            onChange={(event) => setNewPerformance({
                                                ...newPerformance,
                                                remark: event.target.value
                                            })}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControlLabel control={<Checkbox defaultChecked={performance?.alwaysAvailable} onChange={(event) => {
                                            setNewPerformance({
                                                ...newPerformance,
                                                alwaysAvailable: event.target.checked
                                            })
                                        }} />} label={resourceFinal[appLanguage].words.ALWAYS_AVAILABLE} />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControlLabel control={<Checkbox defaultChecked={addNew ? defaultActivity?.nonSubscribable : performance?.studentRegistrationDisabled} onChange={(event) => {
                                            setNewPerformance({
                                                ...newPerformance,
                                                studentRegistrationDisabled: event.target.checked
                                            })
                                        }} />} label={resourceFinal[appLanguage].words.NONSUBSCRIBABLE} />
                                    </Grid>
                                    {showFailed && <Grid size={{ xs: 12 }}>
                                        <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}</Typography>
                                    </Grid>}
                                    {(errorLocation || errorSubscribableFrom || errorSubscribableUntil || errorMomentStart || errorMomentEnd) && <Grid size={{ xs: 12 }}>
                                        <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.ERROR_PROPERTIES_EMPTY}
                                            {errorSubscribableFrom && <li>{resourceFinal[appLanguage].words.SUBSCRIBABLE_FROM}</li>}
                                            {errorSubscribableUntil && <li>{resourceFinal[appLanguage].words.SUBSCRIBABLE_UNTIL}</li>}
                                            {errorMomentStart && <li>{resourceFinal[appLanguage].words.MOMENT} {resourceFinal[appLanguage].words.START.toLowerCase()}</li>}
                                            {errorMomentEnd && <li>{resourceFinal[appLanguage].words.MOMENT} {resourceFinal[appLanguage].words.END.toLowerCase()}</li>}
                                        </Typography>
                                    </Grid>}
                                    {(performance?.moments > 1 && !errorMomentRange && !errorSubscribableMoment && !addNew) && <Grid size={{ xs: 12 }}><Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.WARNING_ALL_MOMENTS}</Typography></Grid>}
                                    {errorMoments && <Grid size={{ xs: 12 }}><Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.WARNING_NO_MOMENTS}</Typography></Grid>}
                                    {errorMomentRange && <Grid size={{ xs: 12 }}><Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.WARNING_RANGE_MOMENTS}</Typography></Grid>}
                                    {errorSubscribableRange && <Grid size={{ xs: 12 }}><Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.WARNING_RANGE_SUBSCRIBABLE}</Typography></Grid>}
                                    {errorMomentPeriodRange && <Grid size={{ xs: 12 }}><Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.WARNING_RANGE_PERIODMOMENT}</Typography></Grid>}
                                    {(!errorMomentRange && !errorSubscribableRange && !errorMomentPeriodRange && errorSubscribableMoment) && <Grid size={{ xs: 12 }}><Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.WARNING_SUBSCRIBABLE_MOMENT}</Typography></Grid>}
                                    <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <ButtonGroup>
                                            <Button variant="outlined" color="error" onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                                            {!addNew && <Button disabled={!changed || errorMomentRange || errorSubscribableRange || errorSubscribableMoment || errorMomentPeriodRange} variant="outlined" color="primary" onClick={updateClick}>{resourceFinal[appLanguage].words.SAVE}</Button>}
                                            {addNew && <Button variant="outlined" color="primary" onClick={addClick}>{resourceFinal[appLanguage].words.ADD}</Button>}
                                        </ButtonGroup>
                                    </Grid>
                                </>
                            }
                        />
                    </Grid>
                </Scrollbars>
            </Card>

            <Modal open={openLocation} onClose={callbackModalLocation}>
                <>
                    <LocationEditCard handleClose={callbackModalLocation} addNew planningmode />
                </>
            </Modal>
        </Box>
    );
}